<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'

interface Props {
  /** Unique ID for SVG defs (needed when multiple nodes on page) */
  id?: string
  /** Frame variant: 'scene' | 'spotify' | 'music' | 'default' */
  variant?: 'scene' | 'spotify' | 'music' | 'default'
  /** Whether the node is active/playing (shows gold styling) */
  isActive?: boolean
  /** Whether to show the glow effect (only when active) */
  showGlow?: boolean
  /** Whether to show corner accents (default: false, only shown when active) */
  showCorners?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: 'node',
  variant: 'default',
  isActive: false,
  showGlow: false,
  showCorners: undefined
})

// Only show corners when active (for all node types)
const shouldShowCorners = computed(() => {
  if (props.showCorners !== undefined) return props.showCorners
  return props.isActive
})

// Use a unique ID for SVG defs to prevent conflicts
const uniqueId = computed(() => `${props.id}-${Math.random().toString(36).slice(2, 9)}`)

// Container ref for measuring actual size
const containerRef = ref<HTMLDivElement | null>(null)
const measuredWidth = ref(200)
const measuredHeight = ref(64)

// Observe size changes
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (containerRef.value) {
    // Initial measurement
    measuredWidth.value = containerRef.value.offsetWidth
    measuredHeight.value = containerRef.value.offsetHeight
    
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        measuredWidth.value = entry.contentRect.width
        measuredHeight.value = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

// Design constants - scale chamfer based on size
const chamferRatio = 0.08 // 8% of the smaller dimension
const minChamfer = 6
const maxChamfer = 14
const strokeWidth = 2

// Calculate chamfer based on current size
const chamfer = computed(() => {
  const minDim = Math.min(measuredWidth.value, measuredHeight.value)
  return Math.max(minChamfer, Math.min(maxChamfer, minDim * chamferRatio))
})

// Calculate frame path with 45° chamfered corners
const framePath = computed(() => {
  const w = measuredWidth.value
  const h = measuredHeight.value
  const c = chamfer.value
  
  return `
    M ${c} 0
    L ${w - c} 0
    L ${w} ${c}
    L ${w} ${h - c}
    L ${w - c} ${h}
    L ${c} ${h}
    L 0 ${h - c}
    L 0 ${c}
    Z
  `
})

// Background fill path (same as frame path but for fill)
const backgroundPath = computed(() => framePath.value)

// Gradient colors based on variant and active state
// When inactive: use border color from theme
// When active: use gold gradient from CSS variables
const gradientColors = computed(() => {
  if (props.isActive) {
    return { start: 'var(--gold-start)', mid: 'var(--gold-mid)', end: 'var(--gold-end)' }
  }
  // Inactive: use theme border color - same color for all stops for solid look
  return { start: 'hsl(var(--border))', mid: 'hsl(var(--border))', end: 'hsl(var(--border))' }
})

// Background color - scenes stay solid, audio nodes get slight tint when active
const backgroundColor = computed(() => {
  if (props.variant === 'scene') {
    return 'hsl(var(--card))'
  }
  if (props.isActive) {
    return 'hsl(var(--card) / 0.95)'
  }
  return 'hsl(var(--card))'
})

// Glow blur amount - only visible when active
const glowBlur = computed(() => props.isActive ? 3 : 0)

// Whether to show the outer glow
const shouldShowGlow = computed(() => props.isActive && props.showGlow)
</script>

<template>
  <div
    ref="containerRef"
    class="node-frame-container"
    :class="{ 
      'node-frame-active': isActive,
      [`node-frame-${variant}`]: true
    }"
  >
    <!-- Glow layer (behind the frame) - only when active -->
    <div 
      v-if="shouldShowGlow" 
      class="node-glow-layer node-glow-active"
    >
      <svg
        class="node-glow-svg"
        :viewBox="`0 0 ${measuredWidth} ${measuredHeight}`"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient :id="`${uniqueId}-glow-gradient`" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" :stop-color="gradientColors.start" />
            <stop offset="50%" :stop-color="gradientColors.mid" />
            <stop offset="100%" :stop-color="gradientColors.end" />
          </linearGradient>
          <filter :id="`${uniqueId}-outer-glow`" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" :stdDeviation="isActive ? 8 : 4" result="blur" />
          </filter>
        </defs>
        <path
          :d="backgroundPath"
          :fill="`url(#${uniqueId}-glow-gradient)`"
          :filter="`url(#${uniqueId}-outer-glow)`"
          opacity="0.4"
        />
      </svg>
    </div>
    
    <!-- Main SVG Frame -->
    <svg
      class="node-frame-svg"
      :viewBox="`0 0 ${measuredWidth} ${measuredHeight}`"
      preserveAspectRatio="none"
    >
      <defs>
        <!-- Gradient for inactive frame stroke (uses theme border color) -->
        <linearGradient :id="`${uniqueId}-inactive`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="hsl(var(--border))" />
          <stop offset="50%" stop-color="hsl(var(--border))" />
          <stop offset="100%" stop-color="hsl(var(--border))" />
        </linearGradient>
        
        <!-- Gradient for active frame stroke (gold) -->
        <linearGradient :id="`${uniqueId}-gold`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" :stop-color="gradientColors.start" />
          <stop offset="50%" :stop-color="gradientColors.mid" />
          <stop offset="100%" :stop-color="gradientColors.end" />
        </linearGradient>
        
        <!-- Inner glow filter -->
        <filter :id="`${uniqueId}-glow`" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" :stdDeviation="glowBlur" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        <!-- Clip path for content -->
        <clipPath :id="`${uniqueId}-clip`">
          <path :d="backgroundPath" />
        </clipPath>
      </defs>
      
      <!-- Background fill -->
      <path
        :d="backgroundPath"
        :fill="backgroundColor"
        class="node-background"
      />
      
      <!-- Inactive border frame (blue-gray) -->
      <path
        :d="framePath"
        fill="none"
        :stroke="`url(#${uniqueId}-inactive)`"
        :stroke-width="strokeWidth"
        stroke-linecap="square"
        stroke-linejoin="miter"
        class="node-border node-border-inactive"
        :class="{ 'node-border-hidden': isActive }"
      />
      
      <!-- Active border frame (gold) -->
      <path
        :d="framePath"
        fill="none"
        :stroke="`url(#${uniqueId}-gold)`"
        :stroke-width="strokeWidth"
        stroke-linecap="square"
        stroke-linejoin="miter"
        :filter="`url(#${uniqueId}-glow)`"
        class="node-border node-border-active"
        :class="{ 'node-border-hidden': !isActive }"
      />
    </svg>

    <!-- Corner accents (inner decorative corners) - hidden for scene nodes -->
    <template v-if="shouldShowCorners">
      <div class="node-corner node-corner-tl" :style="{ borderColor: gradientColors.start }"></div>
      <div class="node-corner node-corner-tr" :style="{ borderColor: gradientColors.start }"></div>
      <div class="node-corner node-corner-bl" :style="{ borderColor: gradientColors.start }"></div>
      <div class="node-corner node-corner-br" :style="{ borderColor: gradientColors.start }"></div>
    </template>
    
    <!-- Content slot with clip path -->
    <div 
      class="node-frame-content"
      :style="{ clipPath: `url(#${uniqueId}-clip)` }"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.node-frame-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.node-glow-layer {
  position: absolute;
  inset: -12px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 400ms ease;
}

/* Only show glow when active, not on hover */
.node-glow-active {
  opacity: 1;
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.node-glow-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.node-frame-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.node-background {
  transition: fill 200ms ease;
}

.node-border {
  vector-effect: non-scaling-stroke;
  transition: opacity 400ms ease;
}

.node-border-inactive {
  opacity: 1;
}

.node-border-active {
  opacity: 1;
}

.node-border-hidden {
  opacity: 0;
}

/* Corner accents - only visible when active */
.node-corner {
  position: absolute;
  width: 10px;
  height: 10px;
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity 400ms ease;
}

.node-frame-active .node-corner {
  opacity: 1;
}

.node-corner-tl {
  top: 10px;
  left: 10px;
  border-top: 1.5px solid;
  border-left: 1.5px solid;
}

.node-corner-tr {
  top: 10px;
  right: 10px;
  border-top: 1.5px solid;
  border-right: 1.5px solid;
}

.node-corner-bl {
  bottom: 10px;
  left: 10px;
  border-bottom: 1.5px solid;
  border-left: 1.5px solid;
}

.node-corner-br {
  bottom: 10px;
  right: 10px;
  border-bottom: 1.5px solid;
  border-right: 1.5px solid;
}

/* Content */
.node-frame-content {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* All variants use gold corners */
.node-corner {
  border-color: var(--gold-solid);
}
</style>
